import type {
  ItemMeta,
  PairStatus,
  SessionSnapshot,
} from "../../shared/types";

export { normalizeCode } from "../../shared/qr";

export const SESSION_KEY = "share.sessionCode";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export class UploadCancelledError extends Error {
  constructor() {
    super("Upload abgebrochen.");
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError("Server nicht erreichbar.", 0);
  }
  if (!res.ok) {
    let message = `Fehler ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keine JSON-Antwort
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

function jsonBody(data: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
}

export const api = {
  createSession: () =>
    request<SessionSnapshot>("/api/sessions", { method: "POST" }),
  getSession: (code: string) =>
    request<SessionSnapshot>(`/api/sessions/${encodeURIComponent(code)}`),
  endSession: (code: string) =>
    request<{ ok: true }>(`/api/sessions/${encodeURIComponent(code)}`, {
      method: "DELETE",
    }),
  // XMLHttpRequest statt fetch, damit es Upload-Fortschritt gibt.
  // fraction ist null, wenn der Browser keinen Fortschritt liefert
  // (z. B. Chrome hinter manchen HTTP/2- oder HTTP/3-Proxys).
  uploadFiles: (
    code: string,
    files: File[],
    options?: {
      onProgress?: (info: {
        fraction: number | null;
        loaded: number;
        total: number;
      }) => void;
      signal?: AbortSignal;
    },
  ) =>
    new Promise<void>((resolve, reject) => {
      const { onProgress, signal } = options ?? {};
      if (signal?.aborted) {
        reject(new UploadCancelledError());
        return;
      }
      const xhr = new XMLHttpRequest();
      signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.upload.onprogress = (event) => {
        onProgress?.({
          fraction:
            event.lengthComputable && event.total > 0
              ? event.loaded / event.total
              : null,
          loaded: event.loaded,
          total: event.total,
        });
      };
      xhr.open("POST", `/api/sessions/${encodeURIComponent(code)}/files`);
      xhr.responseType = "json";
      xhr.onabort = () => reject(new UploadCancelledError());
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        const body = xhr.response as { error?: string } | null;
        reject(new ApiError(body?.error ?? `Fehler ${xhr.status}`, xhr.status));
      };
      xhr.onerror = () => reject(new ApiError("Server nicht erreichbar.", 0));
      const form = new FormData();
      for (const file of files) form.append("files", file, file.name);
      xhr.send(form);
    }),
  addText: (code: string, kind: "note" | "clip", text: string) =>
    request<ItemMeta>(
      `/api/sessions/${encodeURIComponent(code)}/notes`,
      jsonBody({ kind, text }),
    ),
  deleteItem: (code: string, id: string) =>
    request<{ ok: true }>(
      `/api/sessions/${encodeURIComponent(code)}/items/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  restoreItem: (code: string, id: string) =>
    request<{ ok: true }>(
      `/api/sessions/${encodeURIComponent(code)}/items/${encodeURIComponent(id)}/restore`,
      { method: "POST" },
    ),
  createPairing: () => request<{ token: string }>("/api/pair", { method: "POST" }),
  getPairing: (token: string) =>
    request<PairStatus>(`/api/pair/${encodeURIComponent(token)}`),
  completePairing: (token: string, code: string) =>
    request<{ ok: true }>(
      `/api/pair/${encodeURIComponent(token)}/complete`,
      jsonBody({ code }),
    ),
};

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Unbekannter Fehler.";
}

export function formatCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;
}

export function joinUrl(code: string): string {
  return `${location.origin}/j/${code}`;
}

export function pairUrl(token: string): string {
  return `${location.origin}/pair/${token}`;
}

export function downloadUrl(code: string, id: string, inline = false): string {
  return `/api/sessions/${encodeURIComponent(code)}/files/${encodeURIComponent(id)}${inline ? "?inline" : ""}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const today = new Date();
  const time = date.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (date.toDateString() === today.toDateString()) return time;
  return `${date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${time}`;
}
